/**
 * Small, shared bits of ticket vocabulary: status, type, priority.
 *
 * Status is never colour alone (CLAUDE.md 14.2): every pill carries its word, and the
 * type carries an icon plus a label wherever there is room.
 */
import { Bug, HelpCircle, Lightbulb, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TicketPriority, TicketStatus, TicketType } from '@/api/support'
import { cn } from '@/lib/cn'

const STATUS_TONE: Record<TicketStatus, string> = {
  open: 'border-accent text-accent',
  in_progress: 'border-[var(--status-warn)] text-[var(--status-warn)]',
  resolved: 'border-[var(--status-pass)] text-[var(--status-pass)]',
  closed: 'border-border text-text-muted',
}

export function TicketStatusPill({
  status,
  className,
}: {
  status: TicketStatus
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        STATUS_TONE[status],
        className,
      )}
    >
      {t(`support.status.${status}`)}
    </span>
  )
}

const PRIORITY_TONE: Record<TicketPriority, string> = {
  low: 'border-border text-text-muted',
  normal: 'border-border text-text',
  high: 'border-[var(--status-warn)] text-[var(--status-warn)]',
  urgent: 'border-[var(--status-fail)] text-[var(--status-fail)]',
}

export function TicketPriorityPill({ priority }: { priority: TicketPriority }) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs',
        PRIORITY_TONE[priority],
      )}
    >
      {t(`support.priority.${priority}`)}
    </span>
  )
}

export const TICKET_TYPE_ICON: Record<TicketType, LucideIcon> = {
  issue: Bug,
  feature: Lightbulb,
  question: HelpCircle,
}

export function TicketTypeIcon({ type, size = 16 }: { type: TicketType; size?: number }) {
  const { t } = useTranslation()
  const Icon = TICKET_TYPE_ICON[type]
  return (
    <Icon size={size} aria-label={t(`support.type.${type}`)} className="shrink-0 text-text-muted" />
  )
}

/** "Waiting for support" / "Support replied", from the reporter's chair. */
export function TicketBallPill({
  awaitingStaff,
  messageCount,
}: {
  awaitingStaff: boolean
  messageCount: number
}) {
  const { t } = useTranslation()
  if (awaitingStaff) {
    return <span className="text-xs text-text-muted">{t('support.ball.awaiting_support')}</span>
  }
  if (messageCount === 0) return null
  return (
    <span className="rounded-full bg-[var(--status-pass-bg)] px-2 py-0.5 text-xs font-medium text-[var(--status-pass)]">
      {t('support.ball.support_replied')}
    </span>
  )
}
